import { communicationService } from './communicationService';
import { logger } from './utils';

/**
 * Domain-specific service for story operations
 * Uses the unified communication service
 */
export class StoryService {
  constructor(commsService) {
    this.http = commsService.httpClient;
    this.ws = commsService.wsManager;
  }
  
  /**
   * Fetch all stories for the user
   * @param {Object} options - Additional options for the request
   * @returns {Promise<Array>} Array of stories
   */
  async getStories(options = {}) {
    try {
      const response = await this.http.get('/stories/', options);
      return response.data || [];
    } catch (error) {
      logger.error('StoryService', 'Failed to fetch stories', error);
      throw error;
    }
  }
  
  /**
   * Fetch a single story by ID using WebSocket with HTTP fallback
   * @param {string|number} storyId - ID of the story to fetch
   * @param {boolean} force - Force refresh even if already loaded
   * @returns {Promise<Object>} Story data
   */
  async getStory(storyId, force = false) {
    // Always try WebSocket first if connected
    try {
      if (this.ws.isConnected()) {
        logger.info('StoryService', `Fetching story ${storyId} via WebSocket`);
        const result = await this.ws.joinStory(storyId, null, force);
        // If WebSocket joinStory returns a story directly, use it
        if (result && typeof result === 'object' && result.id) {
          return result;
        }
      }
    } catch (error) {
      logger.warn('StoryService', `WebSocket fetch failed for story ${storyId}, falling back to HTTP`, error);
      // Fall through to HTTP fallback
    }
    
    // HTTP fallback - auth headers already applied by the communication service
    logger.info('StoryService', `Fetching story ${storyId} via HTTP`);
    const response = await this.http.get(`/story/${storyId}/state/`);
    return response.data;
  }
  
  /**
   * Create a new story
   * @param {Object} storyData - The story data to create
   * @returns {Promise<Object>} The created story
   */
  async createStory(storyData) {
    try {
      const response = await this.http.post('/stories/', storyData);
      return response.data;
    } catch (error) {
      logger.error('StoryService', 'Failed to create story', error);
      throw error;
    }
  }
  
  /**
   * Generate a new page for a story
   * @param {string|number} storyId - ID of the story
   * @returns {Promise<Object>} The new page data
   */
  async generatePage(storyId) {
    try {
      const response = await this.http.post(`/stories/${storyId}/pages/`);
      return response.data;
    } catch (error) {
      logger.error('StoryService', `Failed to generate page for story ${storyId}`, error);
      throw error;
    }
  }
  
  /**
   * Toggle sharing status for a story
   * @param {string|number} storyId - ID of the story
   * @returns {Promise<Object>} Updated story data with sharing status
   */
  async toggleSharing(storyId) {
    try {
      const response = await this.http.post(`/stories/${storyId}/toggle-sharing/`);
      return response.data;
    } catch (error) {
      logger.error('StoryService', `Failed to toggle sharing for story ${storyId}`, error);
      throw error;
    }
  }
  
  /**
   * Get a story image
   * @param {string|number} pageId - ID of the page
   * @returns {Promise<string>} URL of the image
   */
  async getPageImage(pageId) {
    try {
      // Using fetch directly to get binary data
      const response = await fetch(`${this.http.defaults.baseURL}/pages/${pageId}/image/`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      logger.error('StoryService', `Failed to fetch image for page ${pageId}`, error);
      throw error;
    }
  }
  
  /**
   * Register story event listeners
   * @param {string|number} storyId - ID of the story to listen for
   * @param {Function} callback - Callback function for story updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToStoryUpdates(storyId, callback) {
    if (!storyId || !callback) {
      logger.warn('StoryService', 'Invalid parameters for subscribeToStoryUpdates');
      return () => {};
    }
    
    const handleStoryUpdate = (event) => {
      if (event.detail && event.detail.storyId === parseInt(storyId, 10)) {
        callback(event.detail.story);
      }
    };
    
    // Listen for story updates
    document.addEventListener('storyUpdate', handleStoryUpdate);
    
    // Return unsubscribe function
    return () => {
      document.removeEventListener('storyUpdate', handleStoryUpdate);
    };
  }
}

// Export singleton
export const storyService = new StoryService(communicationService);